/**
 * Hook: POST /api/t/{tenantSlug}/interview-prep — Batch 30j-4, durable rev 2026-06-17.
 *
 * Mutation to START an interview-prep generation. Durable rev: the POST
 * enqueues a durable job_queue row + INSERTs the `interview_prep` row in
 * `status='queued'` (one transaction), and returns `{ id, status: "queued" }`.
 * The APScheduler worker runs the Anthropic call out of band (navigate-away
 * safe); the orchestrator's `useInterviewPrep` POLL picks up the result.
 *
 * On success this optimistically writes a synthesized `queued` row to the
 * React Query cache so the UI flips to the generating surface IMMEDIATELY
 * (no blank flicker between mutation success and the first poll). Then
 * invalidates the polling query so the real row arrives within ~one tick.
 * Mirrors `useStartInterviewAnalysis`.
 *
 * URL note: `/api/t/{tenantSlug}/interview-prep` per tenant-router canon.
 *
 * Hard rules:
 *   - 409 `action_already_running` is a graceful path. Toast + invalidate
 *     the by-resource query so the orchestrator picks up the in-flight row.
 *     Do NOT surface as a red error. No synthetic row written on 409.
 *   - Other errors bubble to the consumer's `onError` for surface decisions.
 *     Never surface raw `err.error` strings (HARD-7); the consumer translates.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useToastStore } from "@/stores/useToastStore";
import type { InterviewPrepRow } from "@/lib/derivePrepPhase";

interface StartArgs {
  candidate_id: string;
  jd_id: string;
  /** Phase 2.6 — per-stage prep generation. Optional; legacy callers pass undefined. */
  stage_id?: string;
}

interface StartPrepResponse {
  id: string;
  status: "queued";
}

export function useStartInterviewPrep(): UseMutationResult<
  StartPrepResponse,
  ApiError,
  StartArgs
> {
  const queryClient = useQueryClient();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<StartPrepResponse, ApiError, StartArgs>({
    mutationFn: async (args) => {
      const body: Record<string, string> = {
        candidate_id: args.candidate_id,
        jd_id: args.jd_id,
      };
      if (args.stage_id) body.stage_id = args.stage_id;
      const r = await apiClient.post<StartPrepResponse>(
        `/api/t/${tenantSlug}/interview-prep`,
        body,
      );
      return r.data;
    },
    onSuccess: (response, vars) => {
      const queryKey = [
        "interview-prep",
        "by-resource",
        tenantSlug,
        vars.candidate_id,
        vars.jd_id,
      ];
      // Optimistic write — synthesize a queued row so the UI flips to the
      // generating surface IMMEDIATELY. The next poll (triggered by the
      // invalidate below) replaces it with the real row within ~one tick.
      const optimisticRow: InterviewPrepRow = {
        id: response.id,
        candidate_id: vars.candidate_id,
        jd_id: vars.jd_id,
        status: response.status,
        content: null,
        model_used: null,
        error: null,
        created_at: new Date().toISOString(),
        completed_at: null,
      };
      queryClient.setQueryData(queryKey, optimisticRow);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err, vars) => {
      // 409 `action_already_running`: a prep is already generating for
      // this (candidate, jd). Toast + invalidate the by-resource query so
      // the orchestrator picks up the in-flight row via poll. No synthetic
      // row is written.
      if (err instanceof ApiError && err.error === "action_already_running") {
        addToast(
          "A prep guide is already generating for this candidate.",
          "warning",
        );
        void queryClient.invalidateQueries({
          queryKey: [
            "interview-prep",
            "by-resource",
            tenantSlug,
            vars.candidate_id,
            vars.jd_id,
          ],
        });
        return;
      }
      // Other errors bubble. Consumer's onError reads `err.details.existing_id`
      // when applicable, decides whether to surface a toast / inline error.
    },
  });
}
